@file:UseSerializers(UuidSerializer::class)

package it.neckar.lizergy.model.configuration.components

import it.neckar.lizergy.lifeCycle.LizergyHasLifeCycle
import it.neckar.lizergy.lifeCycle.LizergyLifeCycleState
import it.neckar.lizergy.model.configuration.quote.builder.BatteryInverter
import it.neckar.lizergy.model.configuration.quote.builder.BatteryStorageExtensionType
import it.neckar.lizergy.model.configuration.quote.builder.BatteryStorageType
import it.neckar.lizergy.model.energy.AmountOfEnergy
import it.neckar.lizergy.model.energy.BatteryCapacity
import it.neckar.uuid.HasValueClassUuid
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.UuidSerializer
import it.neckar.uuid.ValueClassUuid
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import kotlin.uuid.Uuid

/**
 * A battery configuration with an option extension
 */
@Serializable
data class BatteryConfiguration(

  override val id: BatteryConfigurationId,
  /**
   * The battery (optional)
   */
  val battery: BatteryStorageType?,
  /**
   * The (optional) extension. Only makes sense if a battery has been selected, too
   */
  val extension: BatteryStorageExtensionType? = null,

  val inverterType: BatteryInverter,

  ) : HasValueClassUuid, LizergyHasLifeCycle {

  /**
   * The life cycle of this configuration
   */
  override val lifeCycleState: LizergyLifeCycleState
    get() = inverterType.lifeCycleState
      .and(extension?.lifeCycleState)
      .and(battery?.lifeCycleState)

  val totalNumberOfBatteryModules: Int
    get() = (battery?.numberOfBatteryModules ?: 0) + (extension?.numberOfBatteryModules ?: 0)

  /**
   * Returns the total storage
   */
  val totalCapacity: BatteryCapacity
    get() = (battery?.storageCapacity ?: BatteryCapacity.Zero).plus(extension?.storageCapacity ?: BatteryCapacity.Zero)

  val totalStorage: AmountOfEnergy
    get() = totalCapacity.capacity

  val compatibleWithIndependenceManager: Boolean
    get() = battery?.compatibleWithIndependenceManager ?: false

  /**
   * Formats the configuration.
   */
  fun format(): String {
    return "${formatOnlyBattery()} mit ${inverterType.description}"
  }

  fun formatOnlyBattery(): String {
    return buildString {
      if (battery == null) {
        append("Keine Batterie")
      } else {
        append(battery.description)
        if (totalNumberOfBatteryModules > 1) append(" (${totalNumberOfBatteryModules} Module)")
      }
      if (extension != null) append(" + ${extension.description}")
    }
  }

  /**
   * An id for a battery configuration
   */
  @Serializable
  data class BatteryConfigurationId(override val uuid: NativeUuid): ValueClassUuid {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): BatteryConfigurationId {
        return BatteryConfigurationId(randomUuid4())
      }
    }
  }
}
